import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { toast } from 'sonner';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'error';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  status: AuthStatus;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
  onSessionChanged?: (session: Session | null) => void;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ 
  children, 
  onSessionChanged 
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('idle');
  const [isLoading, setIsLoading] = useState(true);
  
  // Track toast state to prevent duplicates
  const toastShownRef = useRef<{ [key: string]: boolean }>({});
  // Track if we're in the middle of a sign-in process
  const isSigningInRef = useRef<boolean>(false);

  const clearError = useCallback(() => {
    setStatus('idle');
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      setStatus('loading');
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error refreshing session:', error);
        setStatus('error');
        return;
      }

      setSession(currentSession);
      setUser(currentSession?.user || null);
      setStatus(currentSession ? 'authenticated' : 'idle');
      
      // Notify parent about session change
      onSessionChanged?.(currentSession);
    } catch (error) {
      console.error('Exception refreshing session:', error);
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  }, [onSessionChanged]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setStatus('loading');
      isSigningInRef.current = true;
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setStatus('error');
        toast.error('Sign-in failed', { id: 'signin' });
        isSigningInRef.current = false;
        return { error };
      }

      setSession(data.session);
      setUser(data.user);
      setStatus('authenticated');
      toast.success('Signed in successfully', { id: 'signin' });
      
      // Notify parent about session change
      onSessionChanged?.(data.session);
      
      // Reset the flag after a short delay to allow the auth state change to be processed
      setTimeout(() => {
        isSigningInRef.current = false;
      }, 1000);
      
      return { error: null };
    } catch (error) {
      console.error('Exception during sign in:', error);
      setStatus('error');
      toast.error('Sign-in failed', { id: 'signin' });
      isSigningInRef.current = false;
      return { error: error as AuthError };
    }
  }, [onSessionChanged]);

  // Renamed to avoid shadowing/redeclaration
  const handleSignUp = useCallback(async (email: string, password: string) => {
    try {
      setStatus('loading');
      toast.loading('Creating your account...', { id: 'signup' });
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        setStatus('error');
        toast.error('Sign-up failed', { id: 'signup' });
        return { error };
      }

      // Check if email confirmation is required
      if (data.user && !data.session) {
        toast.success('Account created! Please check your email to confirm your account.', { id: 'signup' });
        setStatus('idle');
        return { error: null };
      }

      // If session is available, user is automatically signed in
      if (data.session) {
        setSession(data.session);
        setUser(data.user);
        setStatus('authenticated');
        toast.success('Account created and signed in successfully!', { id: 'signup' });
        
        // Notify parent about session change
        onSessionChanged?.(data.session);
      }
      
      return { error: null };
    } catch (error) {
      console.error('Exception during sign up:', error);
      setStatus('error');
      toast.error('Sign-up failed', { id: 'signup' });
      return { error: error as AuthError };
    }
  }, [onSessionChanged]);

  const signOut = useCallback(async () => {
    try {
      setStatus('loading');
      
      const { error } = await supabase.auth.signOut();

      if (error) {
        setStatus('error');
        toast.error('Error signing out', { id: 'signout' });
        return { error };
      }

      setSession(null);
      setUser(null);
      setStatus('idle');
      toast.success('Signed out successfully', { id: 'signout' });
      
      // Notify parent about session change
      onSessionChanged?.(null);
      
      return { error: null };
    } catch (error) {
      console.error('Exception during sign out:', error);
      setStatus('error');
      toast.error('Error signing out', { id: 'signout' });
      return { error: error as AuthError };
    }
  }, [onSessionChanged]);

  useEffect(() => {
    // Get initial session
    refreshSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[AuthContext] Auth state change:', event, !!session, 'isSigningIn:', isSigningInRef.current);
        
        setSession(session);
        setUser(session?.user || null);
        
        // Create a unique key for this auth event to prevent duplicate toasts
        const toastKey = `${event}_${session?.user?.id || 'no-user'}`;
        
        switch (event) {
          case 'SIGNED_IN':
            setStatus('authenticated');
            // Only show toast if we're actually in the middle of signing in
            // This prevents showing the toast when just tabbing back in
            if (isSigningInRef.current && !toastShownRef.current[toastKey]) {
              toast.success('Signed in successfully', { id: 'auth-signin' });
              toastShownRef.current[toastKey] = true;
              // Clear the flag after a delay to allow for future sign-ins
              setTimeout(() => {
                delete toastShownRef.current[toastKey];
              }, 5000);
            }
            break;
          case 'SIGNED_OUT':
            setStatus('idle');
            // Only show toast if we haven't shown one for this sign-out
            if (!toastShownRef.current[toastKey]) {
              toast.success('Signed out successfully', { id: 'auth-signout' });
              toastShownRef.current[toastKey] = true;
              // Clear the flag after a delay
              setTimeout(() => {
                delete toastShownRef.current[toastKey];
              }, 5000);
            }
            break;
          case 'TOKEN_REFRESHED':
            setStatus('authenticated');
            break;
          case 'USER_UPDATED':
            setStatus('authenticated');
            break;
          default:
            setStatus(session ? 'authenticated' : 'idle');
        }
        
        // Notify parent about session change
        onSessionChanged?.(session);
      }
    );

    return () => subscription.unsubscribe();
  }, [refreshSession, onSessionChanged]);

  const value: AuthContextType = {
    user,
    session,
    status,
    isLoading,
    signIn,
    signUp: handleSignUp,
    signOut,
    refreshSession,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
